import { showMessageError, checkNull } from '@/shared/lib';
import { useConstructor, useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useImperativeHandle } from 'react';
import { t } from 'ttag';
import { NEW_STUDY_STEP_1_DEFAULT_DATA } from '../../config/newStudyStep1';
import type { NewStudyStep1Props, NewStudyStep1State } from './newStudyStep1.types';

/**
 * Drives the patient demographics step of the new-study wizard, holding the step 1 form data, its per-field errors and the ethnicity drawer flag in one merged state. Seeds newStudyStep1Data from NEW_STUDY_STEP_1_DEFAULT_DATA merged with defaultData whenever defaultData changes, reports load progress through setIsLoadedData, exposes onClickBottomButton as handleNavigation on ref, scrolls the window and .app-body to the top on mount, and clears errors on any field edit.
 *
 * @param defaultData - Previously captured demographics used to prefill the form, defaulting to empty patient fields.
 * @param setIsLoadedData - Callback flagging whether the step has finished seeding its data.
 * @param handleClick - Wizard callback receiving the validated step1 payload.
 * @param ref - Forwarded ref exposing handleNavigation so the wizard can trigger validation.
 * @returns The step 1 state together with the field-change, submit and ethnicity-drawer handlers.
 */
const useNewStudyStep1 = (
  {
    defaultData = {
      patientId: '',
      firstName: '',
      middleName: '',
      lastName: '',
      dob: '',
      gender: '',
    },
    setIsLoadedData,
    handleClick,
  }: NewStudyStep1Props,
  ref: any,
) => {
  const [state, setState] = useMergeState<NewStudyStep1State>({
    errorObject: {},
    newStudyStep1Data: {
      ...NEW_STUDY_STEP_1_DEFAULT_DATA,
      ...defaultData,
    },
    isOpenEthnicityDrawer: false,
  });

  /**
   * Writes one patient demographic value into the step 1 data of the new-study wizard, refusing over-long entries. Records an 'Invalid value' error and returns early when patientId exceeds 31 characters or firstName, middleName or lastName exceed 63 characters; otherwise the value is assigned onto a copy of newStudyStep1Data.
   *
   * @param name - Key inside newStudyStep1Data to write, also used as the error key.
   * @param value - Value entered for that field, length-checked through its string form.
   * @returns Nothing.
   */
  const onChange = (name: string, value: string | boolean) => {
    if (name === 'patientId' && value.toString().length > 31) {
      const newErrorObject = { ...state.errorObject };
      _.assign(newErrorObject, { patientId: t`Invalid value` });
      setState({ errorObject: newErrorObject });
      return;
    }
    if (
      (name === 'firstName' || name === 'lastName' || name === 'middleName') &&
      value.toString().length > 63
    ) {
      const newErrorObject = { ...state.errorObject };
      _.assign(newErrorObject, { [name]: t`Invalid value` });
      setState({ errorObject: newErrorObject });
      return;
    }

    const updatedNewStudyStep1Data = { ...state.newStudyStep1Data };
    (updatedNewStudyStep1Data as any)[name] = value;
    setState({ newStudyStep1Data: updatedNewStudyStep1Data });
  };

  /**
   * Validates the step 1 patient demographics and hands them to the wizard when they are complete. Runs checkNull over patientId, firstName, lastName and dob, adds range messages for height outside 1 to 9 ft or minutes above 11 in and for weight outside 1 to 800.9 lb, then either shows an error toast and stores errorObject or calls handleClick with the step1 payload.
   *
   * @returns Nothing.
   */
  const onClickBottomButton = () => {
    const checkNullData = [
      {
        field: 'patientId',
        value: state.newStudyStep1Data.patientId,
        isRequired: true,
      },
      {
        field: 'firstName',
        value: state.newStudyStep1Data.firstName,
        isRequired: true,
      },
      {
        field: 'lastName',
        value: state.newStudyStep1Data.lastName,
        isRequired: true,
      },
      {
        field: 'dob',
        value: state.newStudyStep1Data.dob,
        isRequired: true,
      },
    ];
    const errorObject = checkNull(checkNullData) as Record<string, string>;
    if (
      !state.newStudyStep1Data.heightFt ||
      parseInt(state.newStudyStep1Data.heightFt, 10) < 1 ||
      parseInt(state.newStudyStep1Data.heightFt, 10) > 9
    ) {
      _.assign(errorObject, { heightFt: 'Height must be ≥ 1 ft and ≤ 9 ft 11 in' });
    }
    if (state.newStudyStep1Data.heightIn && parseInt(state.newStudyStep1Data.heightIn, 10) > 11) {
      _.assign(errorObject, { heightIn: 'Height must be ≥ 1 ft and ≤ 9 ft 11 in' });
    }
    if (
      !state.newStudyStep1Data.weight ||
      parseFloat(state.newStudyStep1Data.weight) > 800.9 ||
      parseFloat(state.newStudyStep1Data.weight) < 1
    ) {
      _.assign(errorObject, { weight: 'Weight must be ≥ 1 lb and ≤ 800.9 lb' });
    }
    if (!_.isEmpty(errorObject)) {
      showMessageError({
        content: t`Some information is invalid. Please review the highlighted field(s).`,
      });
      setState({ errorObject });
    } else {
      handleClick({ step1: state.newStudyStep1Data }, true);
    }
  };

  /**
   * Shows or hides the ethnicity picker drawer of the new-study step 1 form. Inverts the current isOpenEthnicityDrawer flag and stores it back.
   *
   * @returns Nothing.
   */
  const toggleEthnicityDrawer = () => {
    setState({ isOpenEthnicityDrawer: !state.isOpenEthnicityDrawer });
  };

  useImperativeHandle(ref, () => ({
    handleNavigation: onClickBottomButton,
  }));

  useEffect(() => {
    window.scrollTo(0, 0);
    const bodyElement = document.querySelector('.app-body');
    if (bodyElement) {
      bodyElement.scroll(0, 0);
    }
  }, []);

  useEffect(() => {
    if (_.isEmpty(defaultData)) {
      setState({
        newStudyStep1Data: {
          ...NEW_STUDY_STEP_1_DEFAULT_DATA,
        },
      });
      setIsLoadedData(true);
    } else {
      setState({
        newStudyStep1Data: _.merge(state.newStudyStep1Data, defaultData),
      });
      setIsLoadedData(true);
    }
  }, [defaultData]);

  useConstructor(() => {
    setIsLoadedData(false);
  });

  // *: Clear errorObject when changing input fields
  useUpdateEffect(() => {
    if (!_.isEmpty(state.errorObject)) {
      setState({ errorObject: {} });
    }
  }, [state.newStudyStep1Data]);

  return { state, onChange, onClickBottomButton, toggleEthnicityDrawer };
};

export { useNewStudyStep1 };
